"use client";
import { HTMLAttributes, FC, useState, useRef, useEffect } from "react";
import { Progress } from "../components";
import { YolkLogo } from "./logo";
import { cn } from "../lib/utils";
import { motion } from "motion/react";

type Variant = "default/dashboard" | "default/admin" | "briefing" | "feedback" | "analyzing";
type TrainingLoaderProps = HTMLAttributes<HTMLDivElement> & {
  fullscreen?: boolean;
  variant?: Variant;
};

const PHRASES: Record<Variant, string[]> = {
  'default/admin': [
    "Rounding up the flock's performance data...",
    "Pulling the latest numbers from the incubator...",
    "Gathering ingredients from every rep on the floor...",
    "Counting your chickens (and predicting which ones will hatch)...",
    "Syncing the team's daily scramble...",
  ],
  "default/dashboard": [
    "Pre-heating the sales floor...",
    "Gathering the ingredients for your training session...",
    "Cracking open the database...",
    "Waking up the chickens...",
    "Ensuring the sales funnel is non-stick...",
    "Calibrating the yolk-to-white ratio...",
  ],
  "briefing": [
    "Whisking together a particularly stubborn personality...",
    "Adding a pinch of skepticism and a dash of budget constraints...",
    "Hard-boiling some tough objections for you to handle...",
    "Incubating your prospect's backstory...",
    "Ensuring this customer isn't too easy to crack...",
    "Calibrating a prospect designed to test your patience...",
    "Scrambling the demographic data...",
    "Pre-heating the prospect's pain points...",
    "Separating the decision-makers from the gatekeepers...",

  ],
  "feedback": [
    "Checking if your pitch was sunny-side up or a bit scrambled...",
    "Digesting the conversation data...",
    "Hunting for shells in your closing argument...",
    "Calculating if you walked on eggshells or crushed them...",
    "Extracting the nutritional value of your rebuttal...",
    "Poaching your best moments for the highlight reel...",
    "Letting the insights marinate...",
    "Determining if you earned the Golden Egg or just the Goose...",
  ],
  "analyzing": [
    "Candling the team to find hidden cracks in the process...",
    "Separating the Grade-A calls from the cracked shells...",
    "Identifying who’s poaching the leads and who’s scrambling them...",
    "Measuring the temperature of your sales floor...",
    "Detecting which objection is causing the most breakage...",
  ]
}

export const createPseudoProgress = (cb: (num: number) => void) => {
  let step = 0.2;
  let progress = 0;
  let current_progress = 0;
  let interval: ReturnType<typeof setInterval> | null = null;
  let started = false;

  const start = () => {
    started = true;
    interval = setInterval(() => {
      current_progress += step;
      progress = Math.round(
        Math.floor((Math.atan(current_progress) / (Math.PI / 2)) * 100 * 1100) /
        1000,
      );
      if (progress >= 100) {
        if (interval) {
          clearInterval(interval);
        }
      } else if (progress >= 99) {
        step = 0.2;
      }
      if (progress < 100) {
        cb(progress);
      } else {
        cb(99);
        if (interval) clearInterval(interval);
      }
    }, 500);
  };
  const clear = () => {
    started = false;
    cb(100);
    if (interval) clearInterval(interval);
  };
  return {
    start,
    clear,
    started,
  };
};

export const TrainingLoader: FC<TrainingLoaderProps> = ({
  fullscreen = true,
  variant = 'default/dashboard',
  ...props
}) => {
  const [value, setValue] = useState(0);
  const [currentPhraseIdx, setCurrentPhraseIdx] = useState(-1);
  const progress = useRef(createPseudoProgress(setValue));
  const phrases = PHRASES[variant];

  useEffect(() => {
    const _progress = progress.current;
    if (!_progress.started) {
      _progress.start();
    }

    const interval = setInterval(() => {
      setCurrentPhraseIdx(prev => prev + 1 === phrases.length ? 0 : prev + 1)
    }, 3000)
    return () => {
      setValue(0);
      _progress.clear();
      clearInterval(interval)
    };
  }, []);
  return (
    <div
      className={cn(
        "w-full h-full flex flex-col items-center justify-center",
        fullscreen && "z-50 bg-[#fffef2] fixed top-0 left-0",
      )}
      {...props}
    >
      <div className="w-[72px] h-[72px] mb-12">
        <YolkLogo progress={value} />
      </div>
      <Progress color="#F1CD10" value={value} className="w-[320px] mb-4" />
      <motion.span key={currentPhraseIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-[#5b5540] text-sm font-medium">
        {phrases[currentPhraseIdx]}
      </motion.span>
    </div>
  );
};
